import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetSegmented, SetSelect, SetSwitch } from '../SettingsShell/controls';
import { BOOKING_EARLIEST_OPTIONS, BOOKING_FURTHEST_OPTIONS } from '../../booking/mockData';
import type { BookingSettings } from '../../booking/types';

export interface BookingSettingsViewProps {
  data: BookingSettings;
  location?: string;
  onOpenGeneralInfo?: () => void;
  onOpenReasons?: () => void;
  onOpenCancellation?: () => void;
  onTalkToElva?: () => void;
}

/** Settings › Online booking (spec 50 §2). Four cards; explicit Save per card. */
export function BookingSettingsView({
  data,
  location = 'Alamo Heights Dental (San Antonio, TX)',
  onOpenGeneralInfo,
  onOpenReasons,
  onOpenCancellation,
  onTalkToElva,
}: BookingSettingsViewProps) {
  const [s, setS] = useState<BookingSettings>(data);
  const set = <K extends keyof BookingSettings>(k: K, v: BookingSettings[K]) => { setS((p) => ({ ...p, [k]: v })); };

  // Card 01 can't be switched on without a call disclosure AND an Anyone reason.
  const canEnable = s.hasCallDisclosure && s.hasAnyoneReason;

  if (!s.bought) {
    return (
      <div className="mx-auto max-w-[760px]">
        <SettingsPageHeader
          eyebrow="Settings"
          titleLead="Online"
          titleAccent="booking"
          sub="Patients ask for a visit on a public page, and ELVA calls them to confirm before anything is booked."
        />
        <section
          className="overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] px-7 py-8"
          style={{ boxShadow: 'var(--sh-1)' }}
        >
          <h2 className="m-0 text-[17px] font-bold text-[var(--text-primary)]">Online booking isn&rsquo;t on your plan yet</h2>
          <p className="m-0 mt-2 max-w-[560px] text-[13.5px] leading-[1.65] text-[var(--text-muted)]">
            Give patients a page to ask for a visit any time. ELVA calls to confirm, takes the reservation fee, and books
            it &mdash; then reminders, forms and recall treat it as an ordinary appointment.
          </p>
          <button
            type="button"
            onClick={onTalkToElva}
            className="mt-5 rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2.5 text-[13.5px] font-semibold text-white"
          >
            Talk to the ELVA team
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[820px]">
      <SettingsPageHeader
        eyebrow="Settings"
        titleLead="Online"
        titleAccent="booking"
        sub="Patients ask for a visit online. ELVA calls them to confirm before anything is booked."
      />

      <SettingsCards>
        {/* 01 — Online booking */}
        <SettingsCard n="01" title="Online booking" headRight={<SaveBtn />}>
          <SettingsRow
            label="Take requests online"
            desc={<>Patients ask for a visit online. ELVA calls them to confirm before anything is booked.</>}
            control={
              <SetSwitch
                label="Take requests online"
                checked={s.on}
                disabled={!canEnable}
                onChange={(next) => { if (canEnable) set('on', next); }}
              />
            }
            last={!s.on && canEnable}
          />
          {s.on ? (
            <>
              <SettingsRow
                label="Your booking page"
                desc={<>This is the address patients use. ELVA owns it.</>}
                control={
                  <span className="flex items-center gap-2">
                    <code className="rounded-[8px] bg-[var(--surface-sunken)] px-2.5 py-1.5 font-[var(--font-mono)] text-[12.5px] text-[var(--text-secondary)]">
                      {s.bookingUrl}
                    </code>
                    <IconBtn label="Copy" kind="copy" onClick={() => { void navigator.clipboard.writeText(`https://${s.bookingUrl}`); }} />
                    <a
                      href={`https://${s.bookingUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 text-[12.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
                    >
                      <Icon kind="external" size={13} stroke={1.9} /> Open
                    </a>
                  </span>
                }
              />
              <SettingsRow
                last
                label="What can be booked"
                control={
                  <button
                    type="button"
                    onClick={onOpenReasons}
                    className="text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline"
                  >
                    {s.reasonsOnlineCount} reasons can be booked online &rarr;
                  </button>
                }
              />
            </>
          ) : null}
          {!s.hasCallDisclosure ? (
            <SettingsNote tone="amber">
              Add a call disclosure first.{' '}
              <LinkBtn onClick={onOpenGeneralInfo}>General info &rarr;</LinkBtn>
            </SettingsNote>
          ) : null}
          {!s.hasAnyoneReason ? (
            <SettingsNote tone="amber">
              Set at least one reason to <strong>Anyone</strong>.{' '}
              <LinkBtn onClick={onOpenReasons}>Appointment reasons &rarr;</LinkBtn>
            </SettingsNote>
          ) : null}
        </SettingsCard>

        {/* 02 — Times offered */}
        <SettingsCard n="02" title="Times offered" headRight={<SaveBtn />}>
          <SettingsRow
            label="Times offered each day"
            desc={<>How many open times a patient can pick from on any one day.</>}
            control={<Stepper value={s.timesPerDay} min={1} max={12} onChange={(v) => { set('timesPerDay', v); }} ariaLabel="Times offered each day" />}
          />
          <SettingsRow
            label="Earliest a patient can book"
            control={
              <SetSelect
                ariaLabel="Earliest a patient can book"
                value={s.earliest}
                options={BOOKING_EARLIEST_OPTIONS}
                onChange={(v) => { set('earliest', v); }}
                className="w-[180px]"
              />
            }
          />
          <SettingsRow
            last
            label="Furthest ahead"
            control={
              <SetSelect
                ariaLabel="Furthest ahead"
                value={s.furthest}
                options={BOOKING_FURTHEST_OPTIONS}
                onChange={(v) => { set('furthest', v); }}
                className="w-[180px]"
              />
            }
          />
        </SettingsCard>

        {/* 03 — Reservation fee */}
        <SettingsCard n="03" title="Reservation fee" accent="green" headRight={<SaveBtn />}>
          <SettingsRow
            label="Charge a reservation fee"
            desc={<>A small hold on the patient&rsquo;s time, paid by a secure link after the confirmation call.</>}
            control={
              <SetSwitch
                label="Charge a reservation fee"
                checked={s.feeOn}
                disabled={!s.hasElvaPay}
                onChange={(next) => { if (s.hasElvaPay) set('feeOn', next); }}
              />
            }
          />
          {s.feeOn ? (
            <>
              <SettingsRow
                label="Amount"
                control={<Stepper value={s.feeAmount} min={0} max={200} step={5} prefix="$" onChange={(v) => { set('feeAmount', v); }} ariaLabel="Fee amount" />}
              />
              <SettingsRow
                last
                label="Who pays"
                control={
                  <SetSegmented
                    ariaLabel="Who pays"
                    value={s.feeWho}
                    options={[
                      { value: 'new_only', label: 'New patients only' },
                      { value: 'everyone', label: 'Everyone' },
                    ]}
                    onChange={(v) => { set('feeWho', v); }}
                  />
                }
              />
            </>
          ) : null}
          {s.feeOn ? (
            <SettingsNote tone="green">
              It&rsquo;s posted to the patient&rsquo;s account as a credit and comes off their bill at the visit. If they
              don&rsquo;t come, it stays on their account as the missed-appointment fee.{' '}
              <LinkBtn onClick={onOpenCancellation}>Refunds follow your cancellation policy &rarr;</LinkBtn>
            </SettingsNote>
          ) : null}
          {!s.hasElvaPay ? (
            <SettingsNote tone="amber">Connect ELVA Pay to take a fee.</SettingsNote>
          ) : null}
        </SettingsCard>

        {/* 04 — The confirmation call */}
        <SettingsCard n="04" title="The confirmation call" headRight={<SaveBtn />}>
          <SettingsRow
            label="New patients are always called"
            desc={<>ELVA calls every new patient to confirm the request before it becomes an appointment.</>}
            muted
          />
          <SettingsRow
            last
            label="Call returning patients too"
            desc={<>Off by default — a returning patient on file is booked without a call.</>}
            control={<SetSwitch label="Call returning patients too" checked={s.callReturning} onChange={(next) => { set('callReturning', next); }} />}
          />
          <SettingsNote>
            There is no blocked-numbers list. ELVA pauses a number that has missed two confirmation calls in a week, for
            30 days, on its own.
          </SettingsNote>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location={location} />
    </div>
  );
}

/* ── small local helpers ── */

function SaveBtn() {
  return (
    <button
      type="button"
      className="rounded-[9px] bg-[var(--brand-indigo)] px-3.5 py-2 text-[12.5px] font-semibold text-white"
    >
      Save
    </button>
  );
}

function IconBtn({ label, kind, onClick }: { label: string; kind: 'copy'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
    >
      <Icon kind={kind} size={14} stroke={1.9} />
    </button>
  );
}

function LinkBtn({ children, onClick }: { children: React.ReactNode; onClick?: (() => void) | undefined }) {
  return (
    <button type="button" onClick={onClick} className="font-semibold underline hover:no-underline">
      {children}
    </button>
  );
}

function Stepper({
  value,
  min,
  max,
  step = 1,
  prefix,
  onChange,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  onChange: (v: number) => void;
  ariaLabel: string;
}) {
  const dec = () => { onChange(Math.max(min, value - step)); };
  const inc = () => { onChange(Math.min(max, value + step)); };
  return (
    <span className="inline-flex items-center overflow-hidden rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)]">
      <button
        type="button"
        aria-label={`${ariaLabel} down`}
        onClick={dec}
        disabled={value <= min}
        className="flex h-9 w-9 items-center justify-center text-[var(--text-muted)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
      >
        <Icon kind="minus" size={14} stroke={2} />
      </button>
      <span className="min-w-[56px] border-x border-[#e9edf5] px-3 py-[7px] text-center text-[14px] font-semibold text-[var(--text-primary)]">
        {prefix}
        {value}
      </span>
      <button
        type="button"
        aria-label={`${ariaLabel} up`}
        onClick={inc}
        disabled={value >= max}
        className="flex h-9 w-9 items-center justify-center text-[var(--text-muted)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
      >
        <Icon kind="plus" size={14} stroke={2} />
      </button>
    </span>
  );
}
